import React, { useMemo, useState } from "react";
import { Card, IconGlyph, SectionLabel } from "./ProfileDashboardUI";

const toNumber = (value = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatPercent = (value = 0) => `${toNumber(value).toFixed(0)}%`;

const buildCycleTips = (stats = {}) => {
  const cycleTimeDays = toNumber(stats?.savings?.cycleTimeDays);
  const avgQuotes = toNumber(stats?.savings?.avgQuotes);
  const vendorParticipation = toNumber(stats?.savings?.vendorParticipation);
  const pendingApprovals = toNumber(stats?.rfq?.pending);
  const leakageAmount = toNumber(stats?.leakage?.unjustifiedTotal || stats?.leakage?.total);
  const tips = [];

  if (cycleTimeDays > 7) {
    tips.push({
      title: "Reduce RFQ closure delay",
      desc: `Your average cycle is ${cycleTimeDays} days. Start approval preparation before the quotation deadline for high-value RFQs.`,
    });
  } else {
    tips.push({
      title: "Keep cycle time controlled",
      desc: `Your average cycle is ${cycleTimeDays || 0} days. Keep using timely vendor follow-ups and early comparison reviews.`,
    });
  }

  if (avgQuotes > 0 && avgQuotes < 3) {
    tips.push({
      title: "Improve quote competition",
      desc: `Average quotes per RFQ is ${avgQuotes}. Invite a stronger mix from internal vendors and Neevay Marketplace before sending RFQs.`,
    });
  }

  if (vendorParticipation > 0 && vendorParticipation < 60) {
    tips.push({
      title: "Increase vendor participation",
      desc: `Only ${formatPercent(vendorParticipation)} invited vendors are submitting quotes. Follow up before deadline and avoid inviting inactive vendors repeatedly.`,
    });
  }

  if (pendingApprovals > 0) {
    tips.push({
      title: "Clear approval blockers",
      desc: `${pendingApprovals} RFQs are pending approval. Nudge approvers with RFQ value, vendor recommendation, and decision deadline.`,
    });
  }

  if (leakageAmount > 0) {
    tips.push({
      title: "Review non-L1 awards",
      desc: "Some awarded value is higher than L1. Capture reasons like payment terms, delivery, capability, or urgency to keep leakage justified.",
    });
  }

  return tips.slice(0, 5);
};

const buildNudgeDraft = (stats = {}) => {
  const pendingApprovals = toNumber(stats?.rfq?.pending);
  const cycleTimeDays = toNumber(stats?.savings?.cycleTimeDays);
  const savings = stats?.savings?.total || "Rs. 0";

  return [
    "Subject: Approval reminder for pending RFQs",
    "",
    "Hi [Approver Name],",
    "",
    `There ${pendingApprovals === 1 ? "is" : "are"} ${pendingApprovals} RFQ${pendingApprovals === 1 ? "" : "s"} pending for approval in Neevay.`,
    "Please review the pending RFQs and approve/reject with comments so the procurement cycle can move ahead.",
    "",
    "Suggested review priority:",
    "- High value RFQs first",
    "- RFQs nearing project requirement date",
    "- RFQs where vendor selection and negotiation are already completed",
    "",
    `Current dashboard context: average RFQ cycle is ${cycleTimeDays || 0} days and tracked savings is ${savings}.`,
    "",
    "Thanks,",
    "[Your Name]",
  ].join("\n");
};

const InsightCard = ({ icon, iconBg, iconColor, title, desc, cta, onCta, tokens }) => (
  <Card tokens={tokens} className="p-4 flex gap-3">
    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: iconBg }}>
      <IconGlyph name={icon} className="w-4 h-4" color={iconColor} />
    </div>
    <div>
      <p className="text-[13px] font-semibold mb-1" style={{ color: tokens.ink }}>{title}</p>
      <p className="text-[12px] leading-relaxed" style={{ color: tokens.muted }}>{desc}</p>
      <button
        type="button"
        onClick={onCta}
        className="text-[11px] font-medium mt-2 hover:underline"
        style={{ color: tokens.primary }}
      >
        {cta} -&gt;
      </button>
    </div>
  </Card>
);

const rfqTypeOptions = [
  {
    type: "material",
    title: "Material RFQ",
    desc: "Create a requirement for material supply with quantity, unit, brands, and delivery terms.",
  },
  {
    type: "contractor",
    title: "Contractor RFQ",
    desc: "Create a scope-based requirement for contractor work and BOQ-style comparison.",
  },
  {
    type: "labour",
    title: "Labour RFQ",
    desc: "Create labour or item-rate requirements with skill, quantity, and unit details.",
  },
];

const InsightModal = ({ mode, stats, tips, tokens, onClose, onNavigate }) => {
  const [copied, setCopied] = useState(false);
  if (!mode) return null;

  const isNudgeMode = mode === "nudge";
  const isRfqTypeMode = mode === "rfqType";
  const nudgeDraft = buildNudgeDraft(stats);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(nudgeDraft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/35 px-4">
      <div
        className="w-full max-w-2xl rounded-2xl border shadow-2xl"
        style={{ backgroundColor: tokens.card, borderColor: tokens.line }}
      >
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4" style={{ borderColor: tokens.line }}>
          <div>
            <p className="text-[15px] font-semibold" style={{ color: tokens.ink }}>
              {isRfqTypeMode
                ? "Choose RFQ type"
                : isNudgeMode
                  ? "Approval nudge draft"
                  : "Dashboard performance tips"}
            </p>
            <p className="mt-1 text-[12px]" style={{ color: tokens.faint }}>
              {isRfqTypeMode
                ? "Select the requirement type first so the RFQ form opens with the correct structure."
                : isNudgeMode
                ? "Use this as a clean reminder for approvers without leaving the dashboard."
                : "Suggested actions based on this user's RFQ activity and procurement performance."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-[12px] font-semibold"
            style={{ backgroundColor: tokens.cardSoft, color: tokens.muted }}
          >
            Close
          </button>
        </div>

        <div className="max-h-[68vh] overflow-y-auto p-5">
          {isRfqTypeMode ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {rfqTypeOptions.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => {
                    onClose();
                    onNavigate(`/rfqs/create?rfqType=${option.type}`);
                  }}
                  className="rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                  style={{
                    borderColor: tokens.line,
                    backgroundColor: tokens.cardSoft,
                  }}
                >
                  <p className="text-[13px] font-semibold" style={{ color: tokens.ink }}>
                    {option.title}
                  </p>
                  <p className="mt-2 text-[12px] leading-relaxed" style={{ color: tokens.muted }}>
                    {option.desc}
                  </p>
                  <span className="mt-4 inline-flex text-[11px] font-semibold" style={{ color: tokens.primary }}>
                    Start {option.title} -&gt;
                  </span>
                </button>
              ))}
            </div>
          ) : isNudgeMode ? (
            <>
              <pre
                className="whitespace-pre-wrap rounded-xl border p-4 text-[12px] leading-relaxed"
                style={{
                  borderColor: tokens.line,
                  backgroundColor: tokens.cardSoft,
                  color: tokens.ink,
                  fontFamily: "inherit",
                }}
              >
                {nudgeDraft}
              </pre>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
                  style={{ backgroundColor: tokens.brand }}
                >
                  {copied ? "Copied" : "Copy draft"}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              {tips.map((tip, index) => (
                <div
                  key={tip.title}
                  className="rounded-xl border p-4"
                  style={{ borderColor: tokens.line, backgroundColor: tokens.cardSoft }}
                >
                  <p className="text-[12px] font-semibold" style={{ color: tokens.ink }}>
                    {index + 1}. {tip.title}
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed" style={{ color: tokens.muted }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InsightsNudges = ({ stats, tokens, onNavigate }) => {
  const [activeModal, setActiveModal] = useState("");
  const pendingApprovals = stats?.rfq?.pending ?? 0;
  const totalRfqs = stats?.rfq?.total ?? 0;
  const tips = useMemo(() => buildCycleTips(stats), [stats]);
  const insights = [
    {
      icon: "clock",
      iconBg: tokens.warnLight,
      iconColor: tokens.warn,
      title: "Cycle time health",
      desc: `Avg. RFQ cycle is ${stats?.savings?.cycleTimeDays || 0} days. Use dashboard tips to improve closure speed.`,
      cta: "See tips",
      onCta: () => setActiveModal("tips"),
    },
    {
      icon: "alert",
      iconBg: tokens.warnLight,
      iconColor: tokens.warn,
      title: `${pendingApprovals} RFQs pending approval`,
      desc: "Create a concise approval reminder with RFQ context and next action for approvers.",
      cta: "Draft a nudge",
      onCta: () => setActiveModal("nudge"),
    },
    {
      icon: "award",
      iconBg: tokens.successLight,
      iconColor: tokens.success,
      title: `${totalRfqs} RFQs created`,
      desc: "Keep floating category-backed RFQs to improve vendor participation and savings visibility.",
      cta: "Float an RFQ",
      onCta: () => setActiveModal("rfqType"),
    },
  ];

  return (
    <>
      <SectionLabel tokens={tokens}>Insights & nudges</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {insights.map((insight) => <InsightCard key={insight.title} {...insight} tokens={tokens} />)}
      </div>
      <InsightModal
        mode={activeModal}
        stats={stats}
        tips={tips}
        tokens={tokens}
        onClose={() => setActiveModal("")}
        onNavigate={onNavigate}
      />
    </>
  );
};

export default InsightsNudges;
